import { parseDate } from "helpers/date";
import { centsToDollars, parsePrice } from "helpers/price";
import { Label } from "layout/typography/Label";
import { MultiSelect } from "primereact/multiselect";
import { useEffect, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useGetSelectableReportsQuery } from "store/queries/report";

// Sentinel option value for "restrict this user to zero reports". Kept clearly distinct from any
// real report id (which are UUIDs) so it can never collide. Selecting it drives the separate
// `noReportAccess` form flag rather than being stored as a report id.
const NO_REPORTS_VALUE = "__NO_REPORTS__";

// Per-report access for hospital-staff users. Always shown. The selectable set is scoped to the
// accounts currently chosen on the form, so access can never exceed the user's account
// assignment. Three states, all expressed through this one control:
//   - empty selection            => "no restriction" (sees every report in their accounts)
//   - one or more reports chosen => narrowed to exactly those reports
//   - "No reports" chosen         => restricted to zero reports (the `noReportAccess` flag).
// "No reports" is mutually exclusive with picking specific reports.
export function ReportAccess() {
  const { control, watch, getValues, setValue } = useFormContext();

  const accountIds: string[] = watch("accountIds") || [];
  const noReportAccess: boolean = watch("noReportAccess") || false;

  const { data, isFetching } = useGetSelectableReportsQuery(
    { accountIds },
    { skip: accountIds.length === 0 }
  );

  // Reconcile the saved grants against what is actually selectable (published, in-account
  // reports). A previously granted report that is no longer offered — e.g. it became unpublished
  // — would otherwise render as a blank chip. Drop those once the candidate list has loaded so
  // the selection only ever holds valid, displayable reports.
  useEffect(() => {
    if (isFetching || !data || accountIds.length === 0) return;
    const validIds = new Set((data.data || []).map((r) => r.id));
    const current: string[] = getValues("reportAccessIds") || [];
    const pruned = current.filter((id) => validIds.has(id));
    if (pruned.length !== current.length) {
      setValue("reportAccessIds", pruned);
    }
  }, [data, isFetching, accountIds, getValues, setValue]);

  const options = useMemo(() => {
    const reportOptions = (data?.data || []).map((report) => {
      const payout = report.stripePayoutAmountInCents
        ? parsePrice(centsToDollars(report.stripePayoutAmountInCents))
        : null;
      // Mirror the Reports tab "Account/Provider Name" column: report name, else provider name.
      const accountProviderName =
        report.name || report.providerName || "Report";
      const label = [accountProviderName, parseDate(report.reportDate), payout]
        .filter(Boolean)
        .join(" — ");

      return { label, value: report.id };
    });

    // Always offer "No reports" first so an admin can lock a user to zero reports even when the
    // report they'll eventually get hasn't been generated yet (so isn't in the list).
    return [{ label: "No reports", value: NO_REPORTS_VALUE }, ...reportOptions];
  }, [data]);

  return (
    <div className="formgrid grid w-100">
      <div className="field p-fluid col-12">
        <Label htmlFor="reportAccessIds">Accessible Reports</Label>
        <Controller
          name="reportAccessIds"
          control={control}
          render={({ field: { onChange, value } }) => (
            <MultiSelect
              filter
              display="chip"
              id="reportAccessIds"
              className="w-full"
              optionLabel="label"
              optionValue="value"
              options={options}
              value={noReportAccess ? [NO_REPORTS_VALUE] : value || []}
              onChange={(e) => {
                const picked: string[] = e.value || [];
                const nowNone = picked.includes(NO_REPORTS_VALUE);
                const realIds = picked.filter((v) => v !== NO_REPORTS_VALUE);

                if (nowNone && (!noReportAccess || realIds.length === 0)) {
                  // "No reports" just turned on (or it's still the only thing selected):
                  // exclusive — drop any per-report grants.
                  setValue("noReportAccess", true);
                  onChange([]);
                } else {
                  // Either "No reports" is off, or it was on and a real report was picked —
                  // fall back to normal per-report grants and clear the flag.
                  setValue("noReportAccess", false);
                  onChange(realIds);
                }
              }}
              disabled={accountIds.length === 0}
              placeholder={
                isFetching
                  ? "Loading reports..."
                  : "All reports (no restriction)"
              }
              maxSelectedLabels={3}
            />
          )}
        />
        <small className="text-color-secondary">
          {accountIds.length === 0
            ? "Select one or more accounts above to choose reports."
            : 'Leave empty to allow access to all reports in the selected accounts, or pick "No reports" to hide reports entirely.'}
        </small>
      </div>
    </div>
  );
}
